import { getRequestInstance } from "api/services/utils";
import { RequestTypes } from "api/services/enums";
import {
  AdvisoryPurchaseGetByIdDocument,
  AdvisoryPurchasesPaginatedDocument,
  AdvisoryPurchaseCreateDocument,
  AdvisoryPurchaseUpdateDocument,
  AdvisoryPurchaseListPurchaseTypeDocument,
  AdvisoryPurchaseListPurchaseStatusDocument,
  AdvisoryPurchaseGetGroupedDataDocument,
} from "api/generated";

import { Nullable, Optional, ResourceEnum, ScopeEnum } from "config";
import { executeEviction } from "api/utils/cache-eviction";
import { client } from "config/apollo";

import {
  IAdvisoryPurchaseModel,
  IAdvisoryPurchaseCreateVars,
  IAdvisoryPurchasePaginatedResponse,
  IAdvisoryPurchasePaginatedVars,
  IAdvisoryPurchaseUpdateVars,
  IAdvisoryPurchaseGroupedData,
} from "./types";

export async function getAdvisoryPurchaseListPurchaseType(
  advisoryProjectId?: number
): Promise<string[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: AdvisoryPurchaseListPurchaseTypeDocument,
      variables: {
        advisoryProjectId,
      },
      fetchPolicy: "cache-first",
    });

    return res?.data?.advisoryPurchaseListPurchaseType ?? [];
  } catch (error) {
    throw error;
  }
}

export async function getAdvisoryPurchaseGroupedData(
  resource: ResourceEnum,
  scope: ScopeEnum,
  advisoryProjectId: number,
  advisoryBuyerId: number
): Promise<Nullable<IAdvisoryPurchaseGroupedData>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: AdvisoryPurchaseGetGroupedDataDocument,
      variables: {
        resource,
        scope,
        advisoryProjectId,
        advisoryBuyerId,
      },
      fetchPolicy: "network-only",
    });

    return res?.data?.advisoryPurchaseGetGroupedData;
  } catch (error) {
    throw error;
  }
}

export async function getAdvisoryPurchaseListPurchaseStatus(): Promise<
  string[]
> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: AdvisoryPurchaseListPurchaseStatusDocument,
      fetchPolicy: "cache-first",
    });

    return res?.data?.advisoryPurchaseListPurchaseStatus ?? [];
  } catch (error) {
    throw error;
  }
}

export async function getAdvisoryPurchase(
  advisoryPurchaseId: number
): Promise<Optional<IAdvisoryPurchaseModel>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: AdvisoryPurchaseGetByIdDocument,
      variables: {
        id: advisoryPurchaseId,
      },
      fetchPolicy: "cache-first",
    });

    return res?.data?.advisoryPurchaseGetById;
  } catch (error) {
    throw error;
  }
}

export async function getAdvisoryPurchasePaginated(
  variables: IAdvisoryPurchasePaginatedVars
): Promise<Optional<IAdvisoryPurchasePaginatedResponse>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: AdvisoryPurchasesPaginatedDocument,
      variables,
      fetchPolicy: "cache-first",
    });

    return res?.data?.advisoryPurchaseListPaginated;
  } catch (error) {
    throw error;
  }
}

export async function createAdvisoryPurchase(
  variables: IAdvisoryPurchaseCreateVars
): Promise<Optional<{ advisoryPurchaseId: number }>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: AdvisoryPurchaseCreateDocument,
      variables,
      fetchPolicy: "network-only",
    });

    // Evict advisory purchase lists
    executeEviction(client, {
      typename: "AdvisoryPurchase",
      fieldNames: ["advisoryPurchaseListPaginated"],
    });

    return res?.data?.advisoryPurchaseCreate;
  } catch (error) {
    throw error;
  }
}

export async function updateAdvisoryPurchase(
  variables: IAdvisoryPurchaseUpdateVars
): Promise<Optional<{ advisoryPurchaseId: number }>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: AdvisoryPurchaseUpdateDocument,
      variables,
      fetchPolicy: "network-only",
    });

    // Evict updated entity + all lists + getById queries
    executeEviction(client, {
      typename: "AdvisoryPurchase",
      id: variables.advisoryPurchaseId.toString(),
      fieldNames: ["advisoryPurchaseListPaginated", "advisoryPurchaseGetById"],
    });

    return res?.data?.advisoryPurchaseUpdate;
  } catch (error) {
    throw error;
  }
}
